/**
 * Универсальная форма загрузки файлов
 */

import React, { useRef } from 'react';
import { Formik, Form } from 'formik';
import { TextInput } from '../../../components/UI/FormItems';
import { ReactComponent as Upload } from '../../../asssets/icons/upload.svg';
import { LoadingSpinner } from '../components/LoadingSpinner';
import type { FileUploadForm } from '../../../types';

interface FileUploadFormComponentProps {
  title: string;
  fileId: string;
  onSubmit: (formData: FormData) => Promise<void>;
  isLoading: boolean;
  hasExtraFields?: boolean;
  validationSchema?: any;
  description?: string;
  accept?: string;
  children?: React.ReactNode;
}

/**
 * Универсальный компонент формы загрузки файлов
 */
export const FileUploadFormComponent: React.FC<FileUploadFormComponentProps> = ({
  title,
  fileId,
  onSubmit,
  isLoading,
  hasExtraFields = false,
  validationSchema,
  description,
  accept = '.csv,.xlsx,.xls',
  children,
}) => {
  const [fileAttached, setFileAttached] = React.useState(false);
  const [fileName, setFileName] = React.useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialValues: FileUploadForm = {
    quarter: '',
    year: '',
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileAttached(true);
      setFileName(file.name);
    }
  };

  const handleSubmitForm = async (values: FileUploadForm) => {
    if (!fileInputRef.current?.files?.[0]) {
      return;
    }

    const formData = new FormData();
    formData.append('file', fileInputRef.current.files[0]);

    if (hasExtraFields) {
      formData.append('quarter', values.quarter || '');
      formData.append('year', values.year || '');
    }

    await onSubmit(formData);

    // Сброс формы
    setFileAttached(false);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <fieldset>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmitForm}
      >
        {() => (
          <Form>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '15px' }}>{title}</div>

              {description && <div style={{ fontSize: '13px', color: '#666' }}>{description}</div>}

              {hasExtraFields && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <TextInput
                    label="Квартал: "
                    name="quarter"
                    placeholder="1-4"
                    style={{ width: '50px' }}
                  />
                  <TextInput
                    label="Год: "
                    name="year"
                    placeholder="2024"
                    style={{ width: '80px' }}
                  />
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                id={fileId}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept={accept}
              />

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  type="button"
                  style={{ minWidth: '200px' }}
                  onClick={() => {
                    setFileAttached(false);
                    fileInputRef.current?.click();
                  }}
                  disabled={isLoading}
                >
                  {fileName ? `✓ ${fileName}` : 'Выберите файл'}{' '}
                  {!isLoading && <Upload className="icon" />}
                </button>

                {fileAttached && !isLoading && (
                  <button
                    className="btn"
                    type="submit"
                    style={{ minWidth: '180px' }}
                    disabled={isLoading}
                  >
                    📤 Загрузить в базу
                  </button>
                )}

                {isLoading && (
                  <LoadingSpinner size="small" text="Загрузка..." inline />
                )}
              </div>

              {children}
            </div>
          </Form>
        )}
      </Formik>
    </fieldset>
  );
};
